import React, { useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Input } from 'ui-components/components/input';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import messages, { errorMessages } from './messages';
import commonStyles from '../settings-page.module.less';
import style from './style.module.less';
import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';
import { emptyArray } from 'ui-components/default-props/empty';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useCountryConfig } from '../../../hooks/use-country-config';
import { useWeeklyDigestSetting } from '../../../hooks/use-user-setting';
import { Auth0Wrapper } from '../../../services/auth0/Auth0Wrapper';
import { isEmailAddressValid } from '../../../services/email-sanitation/isEmailAddressValid';
import { sanitizeEmailAddress } from '../../../services/email-sanitation/sanitizeEmailAddress';
import { LogService } from '../../../services/log-service';
import { generatePasswordResetLink } from '../../../services/password-reset-link-generator';
import Global from '../../../store/global-selectors';
import { getCurrentUserSuccess, updateUsersList, updateUserSetting } from '../../../store/user/user.actions';
import { updateProfile } from '../api/my-profile';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import SsoAccountManagement from '../components/sso-account-management/sso-account-management';

const getDefaultProfileErrorState = () => ({
  fullName: [],
  email: [],
});

const EmailTooltipMessage = ({ emailErrors = emptyArray, isPrimarySellerEmail }) => {
  if (emailErrors.length) {
    return emailErrors.map((errorMessage) => (
      <li key={errorMessage.description}>
        <FormattedMessage {...errorMessage} />
      </li>
    ));
  }
  if (isPrimarySellerEmail) {
    return <FormattedMessage {...messages.pleaseContactSupport} />;
  }
  return null;
};

// TODO -- BTC: validation could be done per-field onChange, but this implementation mimics what V2 does
// Future iterations should do validation per-field onChange
export const MyProfile = () => {
  const dispatch = useDispatch();

  const userInfo = useSelector(Global.selectUserCurrentUserInfo);
  const sellerUsers = useSelector(Global.selectSellerUsers);
  const [showWeeklyDigestEmail] = useCountryConfig(CountryConfigNames.ShowWeeklyDigestEmail);

  const [fullName, setFullName] = useState(userInfo.name);
  const [email, setEmail] = useState(userInfo.email);
  const [shouldReceiveWeeklyDigest, setShouldReceiveWeeklyDigest] = useState(useWeeklyDigestSetting());
  const [profileErrorMap, setProfileErrorMap] = useState(getDefaultProfileErrorState());
  const setSanitizedEmail = (emailAddress) => setEmail(sanitizeEmailAddress(emailAddress));
  const shouldRenderSsoManagement = Auth0Wrapper.isUsingAuth0Auth();

  const validateProfile = useCallback(() => {
    const errors = getDefaultProfileErrorState();

    if (!fullName) {
      errors.fullName.push(errorMessages.cannotBeEmpty);
    }

    if (!email) {
      errors.email.push(errorMessages.cannotBeEmpty);
    }

    if (!isEmailAddressValid(email)) {
      errors.email.push(errorMessages.invalidEmailSyntax);
    }

    setProfileErrorMap(errors);
    return errors;
  }, [fullName, email]);

  const onSaveProfile = useCallback(async () => {
    const errorMap = validateProfile();
    const hasError = Object.keys(errorMap).reduce((acc, current) => acc.concat(errorMap[current]), []).length > 0;

    if (!hasError) {
      let result;
      try {
        result = await updateProfile(fullName, email, shouldReceiveWeeklyDigest);
      } catch (e) {
        LogService.logError(e);
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.changesFailed} />,
          duration: 3000,
        });
        return;
      }

      // dispatch actions
      dispatch(
        getCurrentUserSuccess({
          ...userInfo,
          name: result.name,
          email: result.email,
        }),
      ); // updates current user
      const updatedUsers = sellerUsers.map((user) => {
        if (user.userId !== userInfo.userId) {
          return user;
        }
        return {
          ...user,
          name: result.name,
          email: result.email,
        };
      });
      dispatch(updateUsersList(updatedUsers)); // updates users for seller
      dispatch(
        updateUserSetting({
          settingName: UserSettings.weeklyDigest.settingName,
          settingValue: result.shouldReceiveWeeklyDigest,
        }),
      );

      NotificationService.showNotification({
        title: '',
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: <FormattedMessage {...messages.changesSaved} />,
        duration: 3000,
      });
    }
  }, [fullName, email, shouldReceiveWeeklyDigest, validateProfile, userInfo, sellerUsers, dispatch]);

  useEffect(() => {
    const maybeLinkingResult = Auth0Wrapper.accountLinkingRequestResult;
    if (!maybeLinkingResult || !maybeLinkingResult.success) return;

    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title: <FormattedMessage {...messages.connectAccountSuccessTitle} values={{ externalAccountName: maybeLinkingResult.idpName }} />,
      content: <FormattedMessage {...messages.connectAccountSuccessMsg} values={{ externalAccountName: maybeLinkingResult.idpName }} />,
      dontHide: true,
    });
  }, []);

  // For fraud reduction, we need to prevent the
  // primary admin user from changing their email address.
  // Internal users (ShipStation employees) can change the
  // primary admin user's email address if needed.
  const sellerEmail = useSelector(Global.selectSellerEmail);
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);
  const isPrimarySellerEmail = sellerEmail === userInfo.email;
  const shouldHideEmailTooltip = !(profileErrorMap.email.length > 0 || (isPrimarySellerEmail && !isSystemAdmin));
  const passwordResetLink = generatePasswordResetLink(userInfo.username);

  return (
    <div className={commonStyles.content}>
      <div className={style.content}>
        <SettingsPageTitle value={messages.title} />
        <h3>
          <FormattedMessage {...messages.personalInformationSubheader} />
        </h3>
        <div className={style.form}>
          <div className={classnames(style['form-item'], style['input-container'])}>
            <label htmlFor='fullName'>
              <FormattedMessage {...messages.fullName} />
            </label>
            <Tooltip shouldHideTooltip={profileErrorMap.fullName.length === 0}>
              <div>
                <Input
                  data-testid='myprofile-fullname'
                  hasError={profileErrorMap.fullName.length > 0}
                  name='fullName'
                  onChange={setFullName}
                  value={fullName}
                />
              </div>
              <TooltipContent>
                {profileErrorMap.fullName.map((errorMessage) => (
                  <li key={errorMessage.description}>
                    <FormattedMessage {...errorMessage} />
                  </li>
                ))}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className={classnames(style['form-item'], style['input-container'])}>
            <label htmlFor='email'>
              <FormattedMessage {...messages.email} />
            </label>
            <Tooltip shouldHideTooltip={shouldHideEmailTooltip}>
              <div>
                <Input
                  data-testid='myprofile-email'
                  disabled={isPrimarySellerEmail ? !isSystemAdmin : null}
                  hasError={profileErrorMap.email.length > 0}
                  name='email'
                  onChange={setSanitizedEmail}
                  value={email}
                />
              </div>
              <TooltipContent>
                <EmailTooltipMessage emailErrors={profileErrorMap.email} isPrimarySellerEmail={isPrimarySellerEmail} />
              </TooltipContent>
            </Tooltip>
          </div>
          {showWeeklyDigestEmail ? (
            <Checkbox
              checked={shouldReceiveWeeklyDigest}
              className={classnames(style['form-item'], style['form-check'])}
              label={<FormattedMessage {...messages.weeklyDigest} />}
              onChange={setShouldReceiveWeeklyDigest}
            />
          ) : null}
          <Button className={classnames(style['form-item'], style['submit-button'])} onClick={onSaveProfile} theme='primary'>
            <FormattedMessage {...messages.updateProfileButton} />
          </Button>
          {shouldRenderSsoManagement ? <SsoAccountManagement postUnlinkHandler={(success) => success} /> : null}
        </div>
        <h3>
          <FormattedMessage {...messages.changePasswordSubheader} />
        </h3>
        <div className={style.form}>
          <Button
            className={classnames(style['form-item'], style['submit-button'])}
            href={passwordResetLink}
            target='_self'
            theme='primary'
          >
            <FormattedMessage {...messages.changePasswordButton} />
          </Button>
        </div>
      </div>
    </div>
  );
};
